import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth, isStaff, isAdmin } from "@/context/AuthContext";
import BrandMark from "@/components/ventrix/Logo";

import Home from "@/pages/public/Home";
import Track from "@/pages/public/Track";
import Quote from "@/pages/public/Quote";
import Service from "@/pages/public/Service";
import { Terms, Privacy } from "@/pages/public/Legal";

import { Login, Register, ForgotPassword } from "@/pages/auth/Auth";

import Dashboard from "@/pages/portal/Dashboard";
import RequestQuote from "@/pages/portal/RequestQuote";
import ShipmentsList from "@/pages/portal/ShipmentsList";
import ShipmentDetail from "@/pages/portal/ShipmentDetail";
import Book from "@/pages/portal/Book";
import Invoices from "@/pages/portal/Invoices";
import AddressBook from "@/pages/portal/AddressBook";
import { Security } from "@/pages/portal/Preferences";

import OpsConsole from "@/pages/ops/OpsConsole";
import RateCards from "@/pages/ops/RateCards";
import Carriers from "@/pages/ops/Carriers";
import Services from "@/pages/ops/Services";
import MapsSettings from "@/pages/ops/MapsSettings";
import CompanySettings from "@/pages/ops/CompanySettings";
import NotificationsSettings from "@/pages/ops/NotificationsSettings";
import PaymentsSettings from "@/pages/ops/PaymentsSettings";
import { Users, AuditLog } from "@/pages/ops/Admin";
import Reports from "@/pages/ops/Reports";
import PaymentsQueue from "@/pages/ops/PaymentsQueue";
import QuoteRequests from "@/pages/ops/QuoteRequests";
import ShipmentEmails from "@/pages/ops/ShipmentEmails";
import OpsInvoices from "@/pages/ops/Invoices";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function StaffOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin(user.role)) return <Navigate to="/ops" replace />;
  return children;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#F4FEFF]">
      <div className="text-center">
        <BrandMark size={56} className="mx-auto" />
        <div className="mt-4 text-[10px] tracking-[0.28em] uppercase font-bold text-[#475569]">Loading Ventrix</div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" toastOptions={{ style: { fontFamily: "IBM Plex Sans" }}} />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Home />} />
            <Route path="/track" element={<Track />} />
            <Route path="/quote" element={<Quote />} />
            <Route path="/services/:slug" element={<Service />} />
            <Route path="/about" element={<Home />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* Customer portal */}
            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/shipments" element={<Protected><ShipmentsList section="customer" /></Protected>} />
            <Route path="/app/shipments/:id" element={<Protected><ShipmentDetail section="customer" /></Protected>} />
            <Route path="/app/book" element={<Navigate to="/app" replace />} />
            <Route path="/app/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/app/quote" element={<Protected><RequestQuote /></Protected>} />
            <Route path="/app/address-book" element={<Protected><AddressBook /></Protected>} />
            <Route path="/app/security" element={<Protected><Security /></Protected>} />

            {/* Ops */}
            <Route path="/ops" element={<StaffOnly><OpsConsole /></StaffOnly>} />
            <Route path="/ops/shipments" element={<StaffOnly><ShipmentsList section="ops" /></StaffOnly>} />
            <Route path="/ops/shipments/:id" element={<StaffOnly><ShipmentDetail section="ops" /></StaffOnly>} />
            <Route path="/ops/book" element={<StaffOnly><Book /></StaffOnly>} />
            <Route path="/ops/rates" element={<StaffOnly><RateCards /></StaffOnly>} />
            <Route path="/ops/carriers" element={<StaffOnly><Carriers /></StaffOnly>} />
            <Route path="/ops/payments" element={<StaffOnly><PaymentsQueue section="ops" /></StaffOnly>} />
            <Route path="/ops/quote-requests" element={<StaffOnly><QuoteRequests section="ops" /></StaffOnly>} />
            <Route path="/ops/email-review" element={<StaffOnly><ShipmentEmails section="ops" /></StaffOnly>} />
            <Route path="/ops/invoices" element={<StaffOnly><OpsInvoices section="ops" /></StaffOnly>} />
            {/* Admin portal */}
            <Route path="/backoffice" element={<AdminOnly><AdminDashboard /></AdminOnly>} />
            <Route path="/backoffice/users" element={<AdminOnly><Users /></AdminOnly>} />
            <Route path="/backoffice/audit" element={<AdminOnly><AuditLog /></AdminOnly>} />
            <Route path="/backoffice/reports" element={<AdminOnly><Reports /></AdminOnly>} />
            <Route path="/backoffice/payments" element={<AdminOnly><PaymentsQueue section="admin" /></AdminOnly>} />
            <Route path="/backoffice/quote-requests" element={<AdminOnly><QuoteRequests section="admin" /></AdminOnly>} />
            <Route path="/backoffice/email-review" element={<AdminOnly><ShipmentEmails section="admin" /></AdminOnly>} />
            <Route path="/backoffice/invoices" element={<AdminOnly><OpsInvoices section="admin" /></AdminOnly>} />
            <Route path="/backoffice/services" element={<AdminOnly><Services /></AdminOnly>} />
            <Route path="/backoffice/settings/payments" element={<AdminOnly><PaymentsSettings /></AdminOnly>} />
            <Route path="/backoffice/settings/company" element={<AdminOnly><CompanySettings /></AdminOnly>} />
            <Route path="/backoffice/settings/notifications" element={<AdminOnly><NotificationsSettings /></AdminOnly>} />
            <Route path="/backoffice/settings/maps" element={<AdminOnly><MapsSettings /></AdminOnly>} />

            {/* Redirects from earlier admin paths (/ops/* and /admin/*) */}
            <Route path="/admin" element={<Navigate to="/backoffice" replace />} />
            <Route path="/admin/*" element={<Navigate to="/backoffice" replace />} />
            <Route path="/ops/reports" element={<Navigate to="/backoffice/reports" replace />} />
            <Route path="/ops/services" element={<Navigate to="/backoffice/services" replace />} />
            <Route path="/ops/users" element={<Navigate to="/backoffice/users" replace />} />
            <Route path="/ops/audit" element={<Navigate to="/backoffice/audit" replace />} />
            <Route path="/ops/settings/maps" element={<Navigate to="/backoffice/settings/maps" replace />} />
            <Route path="/ops/settings/notifications" element={<Navigate to="/backoffice/settings/notifications" replace />} />
            <Route path="/ops/settings/payments" element={<Navigate to="/backoffice/settings/payments" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
